import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { calendarSlot, globalComplianceText } from "@/mocks/demo-flow";

export default function TelehealthConsultationPage() {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-4">
          <Link
            to="/intake/verification"
            className="text-[11px] md:text-xs font-semibold text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            ← Back to Intake
          </Link>
          <Link
            to="/ai-artifacts"
            className="text-[11px] md:text-xs font-semibold text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap border border-accent-200 rounded-full px-3 py-1 hover:bg-accent-50"
          >
            Clinical Portal
          </Link>
        </div>
      </header>

      {/* Main Split Screen */}
      <main className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* LEFT: Live Video Feed — Dr. A. Paliga, MD */}
        <section className="lg:w-1/2 flex flex-col bg-foreground-950 relative min-h-[320px] lg:min-h-0">
          <div className="flex-1 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20medical%20doctor%20conducting%20telehealth%20video%20consultation%20wearing%20white%20coat%20in%20modern%20clinic%20office%20with%20neutral%20background%20soft%20natural%20lighting%20editorial%20photography%20style%20clean%20healthcare%20aesthetic%20warm%20professional%20atmosphere%20genuine%20compassionate%20expression&width=900&height=700&seq=telehealth-physician-v2&orientation=landscape"
              alt="Dr. A. Paliga, MD conducting telehealth consultation"
              className="w-full h-full object-cover object-top opacity-90"
            />

            {/* Scanlines */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)",
              }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

            {/* Live badge + timer */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 backdrop-blur-sm border border-green-500/30">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[10px] md:text-[11px] font-semibold text-green-100 whitespace-nowrap">
                  Live · Secure
                </span>
              </span>
              <span className="px-2.5 py-1.5 rounded-full bg-black/30 backdrop-blur-sm text-[10px] md:text-[11px] font-mono font-semibold text-white whitespace-nowrap">
                {formatTime(elapsed)}
              </span>
            </div>

            {/* Patient PIP */}
            <div className="absolute bottom-4 right-4">
              <div className="w-28 md:w-36 aspect-[3/4] rounded-xl border-2 border-white/20 overflow-hidden bg-foreground-800 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=man%20in%20his%20late%20thirties%20sitting%20comfortably%20at%20home%20on%20a%20couch%20natural%20lighting%20casual%20clothing%20looking%20at%20smartphone%20screen%20engaged%20in%20telehealth%20video%20call%20warm%20home%20interior%20simple%20background%20editorial%20portrait%20style%20genuine%20expression&width=360&height=480&seq=telehealth-patient-pip-v2&orientation=portrait"
                  alt="Patient on video call"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm px-2 py-1">
                  <p className="text-[8px] md:text-[9px] font-semibold text-white text-center whitespace-nowrap">
                    You
                  </p>
                </div>
              </div>
            </div>

            {/* Physician name overlay */}
            <div className="absolute bottom-4 left-4">
              <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black/40 backdrop-blur-sm">
                <span className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/20">
                  <img
                    src={calendarSlot.physicianPhotoUrl}
                    alt={calendarSlot.physician}
                    className="w-full h-full object-cover object-top"
                  />
                </span>
                <div>
                  <p className="text-sm font-bold text-white whitespace-nowrap">
                    {calendarSlot.physician}
                  </p>
                  <p className="text-[10px] text-foreground-300 whitespace-nowrap">
                    Telehealth Evaluation &amp; Clinical Verification ·{" "}
                    {calendarSlot.duration}
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute top-0 left-0 right-0 bottom-0 border-[3px] border-white/10 pointer-events-none" />

            {/* Rec badge */}
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-red-500/20 backdrop-blur-sm border border-red-500/30">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[9px] md:text-[10px] font-semibold text-red-200 uppercase tracking-wider whitespace-nowrap">
                Rec
              </span>
            </div>
          </div>

          {/* Call controls */}
          <div className="h-14 bg-foreground-900 flex items-center justify-center gap-4 px-4">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 cursor-pointer hover:bg-foreground-700 transition-colors">
              <i className="ri-mic-line text-sm"></i>
            </span>
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 text-red-400 cursor-pointer hover:bg-red-500/30 transition-colors">
              <i className="ri-phone-fill text-lg"></i>
            </span>
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-foreground-300 cursor-pointer hover:bg-foreground-700 transition-colors">
              <i className="ri-vidicon-line text-sm"></i>
            </span>
          </div>
        </section>

        {/* RIGHT: Patient Next Step Panel — STRICTLY PATIENT-FACING */}
        <section className="lg:w-1/2 flex flex-col bg-white overflow-y-auto">
          <div className="flex-1 flex flex-col justify-center px-6 md:px-10 py-8 md:py-12">
            {/* Evaluation Complete Card */}
            <div className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-green-50/30 p-6 md:p-8 mb-6 animate-fadeSlideIn">
              <div className="flex items-start gap-4">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                  <i className="ri-check-double-line text-2xl"></i>
                </span>
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-green-800 mb-2">
                    Evaluation Complete!
                  </h2>
                  <p className="text-sm md:text-base text-green-700 leading-relaxed">
                    Your telehealth consultation has been completed
                    successfully. Dr. Paliga has reviewed your file and
                    everything is in order.
                  </p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8 mb-8">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                What happens next?
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                Your clinical intake profile, telehealth assessment, and 3D limb
                metrics are all complete. Now it&apos;s time to connect you with
                a certified prosthetist in your area who can fabricate your
                custom device and get you walking comfortably.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <span className="text-xs md:text-sm text-foreground-600">
                    Intake questionnaire — complete
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <span className="text-xs md:text-sm text-foreground-600">
                    K-Level mobility assessment — complete
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <span className="text-xs md:text-sm text-foreground-600">
                    Telehealth evaluation — complete
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-arrow-right-circle-line text-xs"></i>
                  </span>
                  <span className="text-xs md:text-sm font-semibold text-foreground-800">
                    Find your clinic — next step
                  </span>
                </div>
              </div>

              {/* CTA: Find My Clinic */}
              <button
                type="button"
                onClick={() => navigate("/clinic-search")}
                className="w-full py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-sm"></i>
                </span>
                Find My Clinic
              </button>

              <p className="text-[10px] md:text-[11px] text-foreground-400 text-center mt-3">
                We&apos;ll show you certified prosthetist clinics near you based
                on your ZIP code and specific amputation needs.
              </p>
            </div>

            {/* Security reassurance */}
            <div className="flex items-center gap-2 text-[10px] md:text-[11px] text-foreground-400">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-line text-xs"></i>
              </span>
              <span>
                Your data remains encrypted and HIPAA-compliant throughout this
                process.
              </span>
            </div>
          </div>
        </section>
      </main>

      {/* Compliance Footer */}
      <footer className="w-full bg-background-100 border-t border-background-200 flex-shrink-0">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
              <i className="ri-information-line text-[11px]"></i>
            </span>
            <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
              {globalComplianceText}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
